import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import Chart from "pages/Topology/Chart";
import Toolbar from "pages/Topology/Toolbar";
import Footer from "pages/Topology/Footer";
import TopologyDivider from "pages/Topology/Divider";
import Node from "pages/Topology/Node";
import { deviceTypeOption, lineOption } from "pages/Topology/const";
import mainStyle from "pages/Topology/mainStyle.module.scss";
import icnButtonUp from "assets/images/icons/icn_button_up.svg";
import icnButtonDown from "assets/images/icons/icn_button_down.svg";
import TabPlaceholder from "../components/TabPlaceholder";

const noop = () => {};
const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptySnapshot = (dayIndex) => ({
  dayIndex,
  state: "noSnapshot",
  snapshotAt: null,
  tree: null,
});

const hasTopologyTree = (snapshot) =>
  snapshot.state === "ok" &&
  Array.isArray(snapshot.tree) &&
  snapshot.tree.length > 0;

const formatSnapshotAt = (timestamp, timezone) => {
  if (!timestamp) return "No snapshot";

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(timestamp * 1000));
    const value = Object.fromEntries(
      parts.map(({ type, value: part }) => [type, part]),
    );
    return `${value.year}/${value.month}/${value.day} ${value.hour}:${value.minute}`;
  } catch (_error) {
    return new Date(timestamp * 1000).toLocaleString();
  }
};

const getSnapshotCounts = (snapshot) => {
  const count = { online: 0, total: 0 };

  const visit = (node) => {
    if (node.type !== deviceTypeOption.CLIENT) {
      count.total += 1;
      if (node.status === "online") count.online += 1;
    }
    (node.children || []).forEach(visit);
  };

  (snapshot.tree || []).forEach(visit);
  return count;
};

const selectedFilterValue = (filterOption, type) =>
  filterOption[type]?.options
    ?.find((option) => option.checked)
    ?.value?.toLowerCase() || "";

const filterSnapshotTree = (source, filterOption, eyeOption) => {
  const keyword = (filterOption.keyword || "").toLowerCase();
  const model = selectedFilterValue(filterOption, "model");
  const state = selectedFilterValue(filterOption, "state");

  const visit = (node) => {
    const isClient = node.type === deviceTypeOption.CLIENT;
    const nodeName = (node.name || "").toLowerCase();
    const nodeMac = (node.macAddress || "").toLowerCase();
    const keywordMatch =
      !keyword || nodeName.includes(keyword) || nodeMac.includes(keyword);
    const modelMatch =
      !model || (!isClient && (node.model || "").toLowerCase() === model);
    const stateMatch = !state || (node.status || "").toLowerCase() === state;
    const children = (node.children || []).map(visit).filter(Boolean);

    if (
      isClient &&
      ((node.wireType === "wired" && !eyeOption.wired) ||
        (node.wireType === "wireless" && !eyeOption.wireless) ||
        (node.status === "online" && !eyeOption.onlineClients) ||
        (node.status === "offline" && !eyeOption.offlineClients))
    ) {
      return null;
    }

    return {
      ...node,
      children,
      __match: keywordMatch && modelMatch && stateMatch,
      __showName: isClient ? eyeOption.clientName : eyeOption.deviceName,
      __showMac: eyeOption.macAddress,
    };
  };

  return visit(source);
};

const normalizeRootTree = (root) => {
  const rawTopologyData = [];
  const statistic = { maxLevel: 0, maxChildren: 0 };

  const visit = (source, parentId, level, path) => {
    const type = source.type || "unknown";
    const deviceId = source.deviceId || source.macAddress || path;
    const id = `${type}@${deviceId}`;
    const children = Array.isArray(source.children) ? source.children : [];
    const node = {
      id,
      originId: deviceId,
      parentId,
      type,
      name: source.name || "-",
      model: source.model,
      state: source.status || "offline",
      mac: source.macAddress || "-",
      wired: source.wireType,
      children: [],
      remove: false,
      match: source.__match ?? true,
      showName: source.__showName ?? true,
      showMac: source.__showMac ?? true,
    };

    node["leading-line-color"] = `${parentId || ""}|${id}|${lineOption.COLOR}${
      type === deviceTypeOption.CLIENT ? `|${lineOption.DASH}` : ""
    }`;
    node.topic = ReactDOMServer.renderToStaticMarkup(
      <Node
        parentId={parentId}
        type={node.type}
        state={node.state}
        name={node.name}
        mac={node.mac}
        match={node.match}
        showName={node.showName}
        showMac={node.showMac}
        readOnly
      />,
    );

    statistic.maxLevel = Math.max(statistic.maxLevel, level);
    statistic.maxChildren = Math.max(statistic.maxChildren, children.length);
    rawTopologyData.push(node);
    node.children = children.map((child, index) =>
      visit(child, id, level + 1, `${path}-${index}`),
    );

    return node;
  };

  return {
    tree: [visit(root, null, 1, "root")],
    rawTopologyData,
    statistic,
  };
};

const createStatistic = () => ({
  online: { count: 0, ap: 0, sw: 0, gw: 0, rtr: 0 },
  offline: { count: 0, ap: 0, sw: 0, gw: 0, rtr: 0 },
  wired: { count: 0 },
  wireless: { count: 0 },
  maxLevel: 0,
  maxChildren: 0,
  models: [],
  tags: [],
});

const getStatistic = (roots) => {
  const statistic = createStatistic();
  const models = new Set();

  const visit = (node, level) => {
    const children = Array.isArray(node.children) ? node.children : [];
    statistic.maxLevel = Math.max(statistic.maxLevel, level);
    statistic.maxChildren = Math.max(statistic.maxChildren, children.length);

    if (node.type === deviceTypeOption.CLIENT) {
      if (node.wireType === "wireless") statistic.wireless.count += 1;
      if (node.wireType === "wired") statistic.wired.count += 1;
    } else {
      const state =
        node.status === "online" ? statistic.online : statistic.offline;
      state.count += 1;
      if (node.type === "ap") state.ap += 1;
      if (node.type === "switch") state.sw += 1;
      if (node.type === "gateway") state.gw += 1;
      if (node.type === "router") state.rtr += 1;
      if (node.model) models.add(node.model);
    }

    children.forEach((child) => visit(child, level + 1));
  };

  roots.forEach((root) => visit(root, 1));
  statistic.models = [...models].sort((left, right) =>
    left.localeCompare(right),
  );
  return statistic;
};

const ReadonlyTree = ({ root, chartId }) => {
  const tooltipRef = useRef(null);
  const normalized = useMemo(() => normalizeRootTree(root), [root]);

  return (
    <Chart
      chartId={chartId}
      rawTopologyData={normalized.rawTopologyData}
      tree={normalized.tree}
      statistic={normalized.statistic}
      tooltipRef={tooltipRef}
      onToggleSlider={noop}
    />
  );
};

const IsolatedDevices = ({ roots }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      gap: "24px 40px",
      px: "20px",
      py: "12px",
      minHeight: "104px",
      alignItems: "center",
    }}
  >
    {roots.map((root) => (
      <Node
        key={`${root.type}@${root.deviceId || root.macAddress}`}
        type={root.type}
        state={root.status}
        name={root.name || "-"}
        mac={root.macAddress || "-"}
        match={root.__match ?? true}
        showName={root.__showName ?? true}
        showMac={root.__showMac ?? true}
        readOnly
      />
    ))}
  </Box>
);

const TreeSection = ({ root, chartId, treeIndex }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [displayRoot, setDisplayRoot] = useState(root);
  const statistic = useMemo(() => getStatistic([root]), [root]);

  useEffect(() => {
    setDisplayRoot(root);
  }, [root]);

  const handleSearch = (_treeIndex, filterOption, eyeOption) => {
    setDisplayRoot(filterSnapshotTree(root, filterOption, eyeOption));
  };

  return (
    <Box
      sx={{
        mb: "16px",
        border: "1px solid",
        borderColor: "primary.greyThread",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Box
        className={`${mainStyle.topology} ${collapsed ? mainStyle.topology_collapsed : ""}`}
        sx={{ mb: 0 }}
      >
        <Toolbar
          treeIndex={treeIndex}
          statistic={statistic}
          onSearch={handleSearch}
        />
        <Box
          className={mainStyle["collapse-button"]}
          sx={{
            backgroundImage: `url(${collapsed ? icnButtonDown : icnButtonUp})`,
          }}
          onClick={() => setCollapsed((current) => !current)}
        />
        {collapsed ? (
          <Box className={mainStyle["collapse-content"]}>
            <Node
              type={displayRoot.type}
              state={displayRoot.status}
              name={displayRoot.name || "-"}
              mac={displayRoot.macAddress || "-"}
              match={displayRoot.__match ?? true}
              showName={displayRoot.__showName ?? true}
              showMac={displayRoot.__showMac ?? true}
              readOnly
            />
          </Box>
        ) : (
          <ReadonlyTree root={displayRoot} chartId={chartId} />
        )}
        <TopologyDivider />
        <Footer treeIndex={treeIndex} />
      </Box>
    </Box>
  );
};

const IsolatedDevicesSection = ({ roots }) => {
  const statistic = useMemo(() => getStatistic(roots), [roots]);
  const [displayRoots, setDisplayRoots] = useState(roots);

  useEffect(() => {
    setDisplayRoots(roots);
  }, [roots]);

  const handleSearch = (_treeIndex, filterOption, eyeOption) => {
    setDisplayRoots(
      roots.map((root) => filterSnapshotTree(root, filterOption, eyeOption)),
    );
  };

  return (
    <Box
      sx={{
        mb: "16px",
        border: "1px solid",
        borderColor: "primary.greyThread",
        borderRadius: "16px",
        overflow: "hidden",
      }}
    >
      <Box
        className={`${mainStyle.topology} ${mainStyle.topology__empty}`}
        sx={{ mb: 0 }}
      >
        <Toolbar treeIndex={-1} statistic={statistic} onSearch={handleSearch} />
        <Box className={mainStyle["topology-title"]}>Isolated Device</Box>
        <Box className={mainStyle["topology-isolated-container"]}>
          <IsolatedDevices roots={displayRoots} />
        </Box>
        <TopologyDivider />
        <Footer treeIndex={-1} />
      </Box>
    </Box>
  );
};

const SnapshotSelector = ({
  snapshots,
  selectedDayIndex,
  onSelect,
  timezone,
}) => {
  const theme = useTheme();
  const visibleSnapshots = snapshots.filter(
    (snapshot) => snapshot.state !== "noSnapshot",
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gap: "12px",
        mb: "16px",
      }}
    >
      {visibleSnapshots.map((snapshot) => {
        const selected = snapshot.dayIndex === selectedDayIndex;
        const { online, total } = getSnapshotCounts(snapshot);
        const available =
          snapshot.state === "ok" && Array.isArray(snapshot.tree);

        return (
          <ButtonBase
            key={snapshot.dayIndex}
            onClick={() => onSelect(snapshot.dayIndex)}
            sx={{
              minWidth: 0,
              minHeight: "76px",
              display: "block",
              textAlign: "left",
              border: "1px solid",
              borderColor: selected
                ? theme.palette.primary.main
                : theme.palette.primary.greyThread,
              borderRadius: "6px",
              bgcolor: selected
                ? theme.palette.primary.main
                : theme.palette.primary.white,
              color: selected
                ? theme.palette.primary.white
                : theme.palette.primary.blackPrimary,
              overflow: "hidden",
              "&:hover": {
                bgcolor: selected
                  ? theme.palette.primary.mainHover
                  : theme.palette.primary.lightBlue,
              },
            }}
          >
            <Box sx={{ p: "10px 8px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", fontWeight: 700, color: "inherit" }}
                >
                  {dayNames[snapshot.dayIndex]}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "inherit",
                    opacity: selected ? 1 : 0.6,
                  }}
                >
                  {available ? `${online}/${total} Online` : "No snapshot"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "inherit",
                  opacity: selected ? 1 : 0.6,
                  mt: "14px",
                }}
              >
                {formatSnapshotAt(snapshot.snapshotAt, timezone)}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const EmptyTopology = ({ children }) => <TabPlaceholder title={children} />;

const SnapshotCard = ({ snapshot }) => {
  const roots = Array.isArray(snapshot.tree) ? snapshot.tree : [];
  const trees = roots.filter((root) => root.children?.length > 0);
  const isolatedRoots = roots.filter((root) => !root.children?.length);

  if (snapshot.state !== "ok" || !snapshot.tree) return null;

  return (
    <Box>
      {roots.length === 0 ? (
        <EmptyTopology>No data available</EmptyTopology>
      ) : (
        <>
          {trees.map((root, index) => (
            <TreeSection
              key={`${root.type}@${root.deviceId || root.macAddress}-${index}`}
              root={root}
              chartId={`summary-topology-${snapshot.dayIndex}-tree-${index}`}
              treeIndex={index}
            />
          ))}
          {isolatedRoots.length > 0 && (
            <IsolatedDevicesSection roots={isolatedRoots} />
          )}
        </>
      )}
    </Box>
  );
};

const Topology = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [requestUuid] = useState(uuidv4());
  const [snapshots, setSnapshots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  useEffect(() => {
    if (!isAuthReady) return;

    const summaryReportId = timeRange?.summaryReportId;
    if (!summaryReportId) {
      setSnapshots(null);
      return;
    }

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/topology.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/topology.read";
    const restfulPlaceholders = standalone
      ? { orgId: auth.realm?.id, siteId: auth.sites?.id, summaryReportId }
      : {
          entId: auth.realm?.id,
          orgId: auth.orgs?.id,
          siteId: auth.sites?.id,
          summaryReportId,
        };
    let active = true;

    const load = async () => {
      setLoading(true);
      setSnapshots(null);
      try {
        const { getSendBatchData } = await sendBatch([
          { apiKey, requestUuid, restfulPlaceholders, params: {} },
        ]);
        if (active) {
          const loadedSnapshots =
            getSendBatchData({ requestUuid })?.data?.snapshots || [];
          const firstSnapshotWithTree = loadedSnapshots.find(hasTopologyTree);

          setSnapshots(loadedSnapshots);
          setSelectedDayIndex(firstSnapshotWithTree?.dayIndex ?? 0);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [
    auth,
    isAuthReady,
    requestUuid,
    sendBatch,
    standalone,
    timeRange?.summaryReportId,
  ]);

  const sevenSnapshots = useMemo(() => {
    const snapshotByDay = new Map(
      snapshots?.map((snapshot) => [snapshot.dayIndex, snapshot]),
    );
    return Array.from(
      { length: 7 },
      (_, dayIndex) => snapshotByDay.get(dayIndex) || emptySnapshot(dayIndex),
    );
  }, [snapshots]);
  const selectedSnapshot =
    sevenSnapshots.find((snapshot) => snapshot.dayIndex === selectedDayIndex) ||
    sevenSnapshots[0];

  if (loading || snapshots === null) {
    return (
      <Box sx={{ minHeight: "240px", display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (sevenSnapshots.every((snapshot) => snapshot.state === "noSnapshot")) {
    return <TabPlaceholder title="No data available" />;
  }

  return (
    <Box sx={{ mx: "20px", mb: "20px" }}>
      <SnapshotSelector
        snapshots={sevenSnapshots}
        selectedDayIndex={selectedDayIndex}
        onSelect={setSelectedDayIndex}
        timezone={timeRange?.timezone}
      />
      <SnapshotCard
        key={selectedSnapshot.dayIndex}
        snapshot={selectedSnapshot}
      />
    </Box>
  );
};

export default Topology;
